import {
  AUTONOMY_PRACTITIONER_NPIS,
  autonomyProviderAcceptsDiagnosis,
  hasSleepDiagnosis,
} from "@/configuration/groups/autonomy/configuration";
import type { AutonomyProviderAssignment } from "@/src/lib/workflows/steps/step-outputs";
import type { Referral } from "@/src/models/documents/referral";
import { getPersonName } from "@/src/models/person";

const SLEEP_PROVIDER_NPI = "1184250987";
const OPTUM_SERVE_PROVIDER_NPI = "1821298399";

const CONDITION_ROUTED_PHYSICIAN_NPIS: readonly string[] = [
  "1023083961",
  "1316478373",
  "1528033362",
  "1619943362",
  "1821298399",
  "1841429057",
  "1861786576",
  "1942682653",
  "1972579449",
];

export type ResolveAutonomyProviderAssignmentInput = {
  referral: Pick<Referral, "diagnosisList" | "receivingProvider">;
  addressedProviderNpi: string | null;
  isOptumServe: boolean;
  pickIndex: (count: number) => number;
};

function resolveTargetNpi({
  referral,
  addressedProviderNpi,
  isOptumServe,
  pickIndex,
}: ResolveAutonomyProviderAssignmentInput): string | null {
  if (isOptumServe) return OPTUM_SERVE_PROVIDER_NPI;
  if (hasSleepDiagnosis(referral)) return SLEEP_PROVIDER_NPI;

  const codes = (referral.diagnosisList ?? [])
    .map((diagnosis) => diagnosis.diagnosisCode?.trim())
    .filter((code): code is string => Boolean(code))
    .filter((code) => CONDITION_ROUTED_PHYSICIAN_NPIS.some((npi) => autonomyProviderAcceptsDiagnosis(npi, code)));
  const primaryCode = codes[0];

  if (!primaryCode) return null;

  if (
    addressedProviderNpi &&
    (!CONDITION_ROUTED_PHYSICIAN_NPIS.includes(addressedProviderNpi) ||
      codes.some((code) => autonomyProviderAcceptsDiagnosis(addressedProviderNpi, code)))
  ) {
    return addressedProviderNpi;
  }

  const eligible = CONDITION_ROUTED_PHYSICIAN_NPIS.filter((npi) => autonomyProviderAcceptsDiagnosis(npi, primaryCode));
  const coverage = (npi: string) => codes.filter((code) => autonomyProviderAcceptsDiagnosis(npi, code)).length;
  const bestCoverage = Math.max(...eligible.map(coverage));
  const candidates = eligible.filter((npi) => coverage(npi) === bestCoverage);

  return candidates[pickIndex(candidates.length)] ?? null;
}

export function resolveAutonomyProviderAssignment(
  input: ResolveAutonomyProviderAssignmentInput
): AutonomyProviderAssignment | null {
  const targetNpi = resolveTargetNpi(input);
  const providerName = targetNpi ? AUTONOMY_PRACTITIONER_NPIS[targetNpi]?.name : null;

  if (!targetNpi || targetNpi === input.addressedProviderNpi || !providerName) return null;

  const addressedProviderName =
    (input.addressedProviderNpi ? AUTONOMY_PRACTITIONER_NPIS[input.addressedProviderNpi]?.name : null) ??
    (input.referral.receivingProvider ? getPersonName(input.referral.receivingProvider).trim() : "");

  return {
    providerNpi: targetNpi,
    providerName,
    addressedProviderName: addressedProviderName || null,
  };
}
